import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import KitchenStepper from "@/components/KitchenStepper";
import {
  ChefHat, ForkKnife, Fire, ListChecks, CaretRight, ClockCounterClockwise, Camera
} from "@phosphor-icons/react";

const STAGE_ICON = { Preparation: ForkKnife, Cooking: Fire };
const STAGE_TONE = {
  Preparation: "bg-sky-100 text-sky-700 border-sky-200",
  Cooking: "bg-orange-100 text-orange-700 border-orange-200",
};

function BatchTile({ b, onOpen }) {
  const Icon = STAGE_ICON[b.stage] || ChefHat;
  const p = b.sop_progress;
  const pct = p?.total ? Math.round((p.done / p.total) * 100) : 0;
  return (
    <button
      onClick={() => onOpen(b)}
      className="w-full text-left rounded-xl border border-border bg-card p-4 sm:p-5 hover:border-primary hover:shadow-md transition-all active:scale-[0.99]"
      data-testid={`kitchen-tile-${b.batch_no}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${STAGE_TONE[b.stage] || "bg-muted border-border"}`}>
          <Icon size={24} weight="duotone"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="mono text-xs font-bold">{b.batch_no}</span>
            <Badge variant="secondary" className="text-[10px]">{b.stage}</Badge>
          </div>
          <div className="font-display font-bold text-base leading-tight truncate mt-0.5">{b.sku_name}</div>
          <div className="overline mt-0.5">{b.planned_qty_kg} kg · {b.sku_code}</div>
        </div>
        <CaretRight size={22} className="text-muted-foreground shrink-0 mt-2"/>
      </div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-muted-foreground">Progress</span>
        <span className={`mono text-sm font-bold ${pct === 100 ? "text-emerald-600" : ""}`}>
          {p?.done ?? 0}/{p?.total ?? 0}
        </span>
      </div>
      <Progress value={pct} className="h-2"/>
    </button>
  );
}

export default function Kitchen() {
  const [batches, setBatches] = useState([]);
  const [stage, setStage] = useState("all");
  const [loading, setLoading] = useState(true);
  const [openBatch, setOpenBatch] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kitchen/active-batches");
      setBatches(data);
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = stage === "all" ? batches : batches.filter(b => b.stage === stage);

  const stats = useMemo(() => ({
    prep: batches.filter(b => b.stage === "Preparation").length,
    cook: batches.filter(b => b.stage === "Cooking").length,
    total: batches.length,
  }), [batches]);

  return (
    <div data-testid="kitchen-page">
      <PageHeader
        subtitle="Kitchen · Chef Checklists"
        title="Preparation SOPs"
        actions={
          <div className="flex flex-wrap gap-1 p-1 rounded-lg border border-border bg-muted/30">
            {[["all", "All"], ["Preparation", "Prep"], ["Cooking", "Cook"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setStage(k)}
                      className={`min-w-[60px] text-sm font-semibold px-4 py-2 rounded-md transition-colors ${
                        stage === k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`kitchen-filter-${k.toLowerCase()}`}>
                {lbl}
              </button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Card className="shadow-none"><CardContent className="p-4">
          <div className="overline flex items-center gap-1"><ForkKnife size={12}/>On Prep</div>
          <div className="text-metric text-3xl text-sky-600">{stats.prep}</div>
        </CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4">
          <div className="overline flex items-center gap-1"><Fire size={12}/>On Cooking</div>
          <div className="text-metric text-3xl text-orange-600">{stats.cook}</div>
        </CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4">
          <div className="overline flex items-center gap-1"><ListChecks size={12}/>Active</div>
          <div className="text-metric text-3xl">{stats.total}</div>
        </CardContent></Card>
      </div>

      {loading ? (
        <div className="p-8 text-muted-foreground">Loading kitchen…</div>
      ) : filtered.length === 0 ? (
        <Card className="shadow-none">
          <CardContent className="p-10 text-center">
            <ChefHat size={40} className="text-muted-foreground mx-auto mb-2" weight="duotone"/>
            <div className="font-semibold">No batches in the kitchen right now</div>
            <div className="text-xs text-muted-foreground mt-1">Start a batch from Production Batches to see it here.</div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="text-[11px] text-muted-foreground mb-2 flex items-center gap-1">
            <Camera size={11}/>Tap a batch to open the step-by-step guide
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map(b => <BatchTile key={b.id} b={b} onOpen={setOpenBatch}/>)}
          </div>
        </>
      )}

      <KitchenStepper batch={openBatch} open={!!openBatch}
                       onOpenChange={(o) => !o && setOpenBatch(null)}
                       onComplete={() => { load(); setOpenBatch(null); }}/>
    </div>
  );
}
